import React, { useState, useEffect, useCallback } from 'react';
import {
  Key, Plus, Trash2, Copy, CheckCircle2, AlertTriangle, Clock,
  Eye, EyeOff, Loader2, RefreshCw, Info,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import api from '../utils/api';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function APIKeys() {
  const { user } = useAuth();
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tenantId, setTenantId] = useState('');

  // Create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', scope: 'read', rsvps_read: false });
  const [creating, setCreating] = useState(false);

  // New-key reveal dialog
  const [newKey, setNewKey] = useState(null); // { name, key, key_prefix }
  const [keyVisible, setKeyVisible] = useState(false);
  const [copied, setCopied] = useState(false);

  // Revoke
  const [revoking, setRevoking] = useState(null);

  const loadKeys = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api-keys');
      setKeys(res.data || []);
    } catch { toast.error('Failed to load API keys'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    loadKeys();
    // Tenant ID — try auth user first, fall back to /auth/me
    if (user?.tenant_id) {
      setTenantId(user.tenant_id);
    } else {
      api.get('/auth/me').then(r => setTenantId(r.data?.tenant_id || '')).catch(() => {});
    }
  }, [loadKeys, user]);

  const createKey = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setCreating(true);
    try {
      const scopes = form.scope === 'write'
        ? ['events:read', 'events:write']
        : ['events:read'];
      if (form.rsvps_read) scopes.push('rsvps:read');
      const res = await api.post('/api-keys', { name: form.name, description: form.description, scopes });
      setNewKey(res.data);
      setKeyVisible(false);
      setCopied(false);
      setCreateOpen(false);
      setForm({ name: '', description: '', scope: 'read', rsvps_read: false });
      loadKeys();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create key');
    } finally { setCreating(false); }
  };

  const revokeKey = async (k) => {
    if (!window.confirm(`Revoke "${k.name}"? Any system using this key will immediately lose access.`)) return;
    setRevoking(k.key_id);
    try {
      await api.delete(`/api-keys/${k.key_id}`);
      toast.success(`"${k.name}" revoked`);
      loadKeys();
    } catch { toast.error('Failed to revoke key'); }
    finally { setRevoking(null); }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopied(false), 3000);
    });
  };

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleString() : '—';

  return (
    <div className="flex h-screen bg-slate-50">
      <Sidebar />
      <div className="flex-1 overflow-auto">
        <div className="max-w-3xl mx-auto px-6 py-10 space-y-8">

          {/* Header */}
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
                <Key className="w-7 h-7 text-indigo-600" /> API Keys
              </h1>
              <p className="text-slate-500 mt-1 text-sm">
                Generate keys for third-party platforms (e.g. Gametime) that need read access to your events.
              </p>
            </div>
            <Button onClick={() => setCreateOpen(true)} data-testid="create-key-btn">
              <Plus className="w-4 h-4 mr-1" /> Generate Key
            </Button>
          </div>

          {/* Tenant ID info box */}
          {tenantId && (
            <div className="rounded-xl border border-indigo-100 bg-indigo-50 p-4 flex items-start gap-3">
              <Info className="w-4 h-4 text-indigo-500 mt-0.5 shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-indigo-800 mb-1">Your Tenant ID</p>
                <p className="text-xs text-indigo-600 mb-2">
                  Share this alongside your API key with any third-party developer. It identifies your organisation.
                </p>
                <div className="flex items-center gap-2">
                  <code className="text-xs bg-white border border-indigo-200 rounded px-2 py-1 font-mono text-indigo-900 select-all" data-testid="tenant-id-display">
                    {tenantId}
                  </code>
                  <button onClick={() => { navigator.clipboard.writeText(tenantId); toast.success('Tenant ID copied'); }}
                    className="text-xs text-indigo-600 hover:text-indigo-800 flex items-center gap-1" data-testid="copy-tenant-id">
                    <Copy className="w-3 h-3" /> Copy
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Keys list */}
          {loading ? (
            <div className="flex justify-center py-16 text-slate-400">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : keys.length === 0 ? (
            <div className="bg-white rounded-2xl border-2 border-dashed border-slate-200 p-14 text-center">
              <Key className="w-10 h-10 mx-auto mb-3 text-slate-300" />
              <p className="font-semibold text-slate-600">No API keys yet</p>
              <p className="text-sm text-slate-400 mt-1 mb-5">Generate a key to give a third-party platform access to your events.</p>
              <Button onClick={() => setCreateOpen(true)}>Generate your first key</Button>
            </div>
          ) : (
            <div className="space-y-3" data-testid="api-keys-list">
              {keys.map(k => (
                <div key={k.key_id} className="bg-white rounded-xl border border-slate-200 p-4 flex flex-wrap items-center gap-4"
                  data-testid={`api-key-row-${k.key_id}`}>
                  <div className="w-9 h-9 rounded-lg bg-indigo-50 flex items-center justify-center shrink-0">
                    <Key className="w-4 h-4 text-indigo-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-slate-900">{k.name}</p>
                    {k.description && <p className="text-xs text-slate-400 truncate">{k.description}</p>}
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs text-slate-500">
                      <span className="font-mono bg-slate-100 px-1.5 py-0.5 rounded">{k.key_prefix}…</span>
                      {(k.scopes || []).includes('events:write')
                        ? <span className="bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-medium">Read + Write</span>
                        : <span className="bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">Read only</span>}
                      {(k.scopes || []).includes('rsvps:read') &&
                        <span className="bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded">RSVP access</span>}
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> Created {fmtDate(k.created_at)}</span>
                      {k.last_used_at
                        ? <span className="flex items-center gap-1 text-emerald-600"><CheckCircle2 className="w-3 h-3" /> Last used {fmtDate(k.last_used_at)}</span>
                        : <span className="text-slate-400 italic">Never used</span>}
                    </div>
                  </div>
                  <Button variant="outline" size="sm" className="text-red-500 hover:text-red-700 shrink-0"
                    onClick={() => revokeKey(k)} disabled={revoking === k.key_id}
                    data-testid={`revoke-key-btn-${k.key_id}`}>
                    {revoking === k.key_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                    Revoke
                  </Button>
                </div>
              ))}
            </div>
          )}

          {/* How it works */}
          <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-3 text-sm text-slate-600">
            <p className="font-semibold text-slate-800">How API keys work</p>
            <ul className="list-disc list-inside space-y-1.5">
              <li>Each key grants <strong>read-only access</strong> to your published events via the App Feed API.</li>
              <li>The full key is shown <strong>once only</strong> at creation — copy it immediately and store it securely.</li>
              <li>You can generate multiple keys — one per integration — and revoke any individually.</li>
              <li>Taptile continues to use its own separate key and is unaffected by changes here.</li>
            </ul>
          </div>

        </div>
      </div>

      {/* ── Create key dialog ── */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Generate a new API key</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Name <span className="text-red-500">*</span></Label>
              <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Gametime, Partner App" data-testid="new-key-name-input" />
              <p className="text-xs text-slate-400 mt-1">Helps you identify which integration is using this key.</p>
            </div>
            <div>
              <Label>Description <span className="text-slate-400 text-xs">(optional)</span></Label>
              <Input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="e.g. Pulls match fixtures for the Gametime app" data-testid="new-key-desc-input" />
            </div>
            <div>
              <Label>Access level</Label>
              <div className="mt-2 space-y-2">
                <label className="flex items-start gap-3 cursor-pointer p-3 rounded-lg border border-slate-200 hover:bg-slate-50" data-testid="scope-read-option">
                  <input type="radio" name="scope" value="read" checked={form.scope === 'read'}
                    onChange={() => setForm(f => ({ ...f, scope: 'read' }))} className="mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-slate-800">Read only <span className="text-xs text-slate-400 font-normal ml-1">(recommended)</span></p>
                    <p className="text-xs text-slate-500 mt-0.5">Can fetch and filter events. Cannot create, update or delete.</p>
                  </div>
                </label>
                <label className="flex items-start gap-3 cursor-pointer p-3 rounded-lg border border-amber-200 bg-amber-50/40 hover:bg-amber-50" data-testid="scope-write-option">
                  <input type="radio" name="scope" value="write" checked={form.scope === 'write'}
                    onChange={() => setForm(f => ({ ...f, scope: 'write' }))} className="mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-slate-800">Read + Write</p>
                    <p className="text-xs text-slate-500 mt-0.5">Can also create, update, and delete events. Only share with trusted integrations.</p>
                  </div>
                </label>
              </div>
            </div>
            <div className="rounded-lg border border-slate-200 p-3">
              <label className="flex items-start gap-3 cursor-pointer" data-testid="scope-rsvps-option">
                <input type="checkbox" checked={form.rsvps_read}
                  onChange={e => setForm(f => ({ ...f, rsvps_read: e.target.checked }))} className="mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-slate-800">RSVP list access <span className="text-xs bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded ml-1">rsvps:read</span></p>
                  <p className="text-xs text-slate-500 mt-0.5">Allows reading attendee names, emails and party sizes. Only enable for integrations that need it — this is personal data.</p>
                </div>
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={createKey} disabled={creating} data-testid="create-key-confirm-btn">
              {creating ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Key className="w-4 h-4 mr-1" />}
              Generate Key
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── New key reveal dialog ── */}
      {newKey && (
        <Dialog open={!!newKey} onOpenChange={() => setNewKey(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-emerald-500" /> Key generated — "{newKey.name}"
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="rounded-xl border-2 border-amber-300 bg-amber-50 p-4 flex gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-amber-800 text-sm">Copy this key now</p>
                  <p className="text-xs text-amber-700 mt-0.5">
                    This is the only time the full key will be shown. Once you close this window it cannot be retrieved — you would need to generate a new one.
                  </p>
                </div>
              </div>
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Your API Key</Label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <input
                      readOnly
                      value={newKey.key}
                      type={keyVisible ? 'text' : 'password'}
                      className="w-full font-mono text-xs bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 pr-10 select-all"
                      data-testid="new-key-value"
                    />
                    <button onClick={() => setKeyVisible(v => !v)}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                      {keyVisible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <Button onClick={() => copyToClipboard(newKey.key)} variant="outline"
                    className={copied ? 'border-emerald-400 text-emerald-600' : ''}
                    data-testid="copy-new-key-btn">
                    {copied ? <CheckCircle2 className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    {copied ? 'Copied!' : 'Copy'}
                  </Button>
                </div>
              </div>
              <p className="text-xs text-slate-500">
                Send this key and your <strong>Tenant ID</strong> to the developer.
                Use header <code className="bg-slate-100 px-1 rounded">X-API-Key</code> for the key
                and <code className="bg-slate-100 px-1 rounded">X-Tenant-ID</code> for your Tenant ID.
              </p>
            </div>
            <DialogFooter>
              <Button onClick={() => setNewKey(null)} data-testid="close-new-key-btn">Done — I've saved the key</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
